import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { DollarSign, Plus, Trash2, Search, AlertTriangle, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '../../lib/apiConfig';
import ConfirmModal from '../shared/ConfirmModal';

const API = API_URL;

/**
 * Contractor pay rates.
 *
 * The one place to set what each contractor is paid per resource type. Sean's
 * gap: BOSS stored these rates (on user.resource_type_assignments, via
 * POST /users/:id/resource-types) but had no screen to manage them — so rates
 * were typed in per-contract and kept going missing, which is what produced
 * the $0.00 cost columns. This is that screen; it's the Vendors-page
 * equivalent he was looking for.
 *
 * Backend is unchanged — this wires existing endpoints:
 *   GET  /api/admin/users                          (contractors + their rates, inline)
 *   GET  /api/admin/default-resource-types         (the resource types + sell rates)
 *   POST /api/users/:id/resource-types             ({resource_type_id, cost_rate})
 *   DELETE /api/users/:id/resource-types/:rtId
 */
export default function ContractorRates() {
  const [contractors, setContractors] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingKey, setSavingKey] = useState(null); // `${userId}:${rtId}` mid-save
  const [addingFor, setAddingFor] = useState(null); // userId with an open add-row
  const [addForm, setAddForm] = useState({ resource_type_id: '', cost_rate: '' });
  const [confirm, setConfirm] = useState({ open: false, userId: null, rtId: null, label: '' });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, rtRes] = await Promise.all([
        api.get(`${API}/api/admin/users`),
        api.get(`${API}/api/admin/default-resource-types`),
      ]);
      if (usersRes.ok) {
        const users = await usersRes.json();
        // Only contractors have pay rates. Match the backend's own definition
        // (role === 'contractor'); fall back to the is_contractor flag.
        setContractors(
          (Array.isArray(users) ? users : users.users || []).filter(
            (u) => u.role === 'contractor' || u.is_contractor,
          ),
        );
      } else {
        toast.error("Couldn't load contractors.");
      }
      if (rtRes.ok) {
        const data = await rtRes.json();
        setResourceTypes(data.default_resource_types || []);
      }
    } catch (err) {
      toast.error("Couldn't load contractor rates.");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contractors;
    return contractors.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q),
    );
  }, [contractors, search]);

  const ratesOf = (c) => c.resource_type_assignments || [];
  // A usable rate is one above zero. A contractor with no assignments AND a
  // contractor whose only rate is $0.00 are the same payroll problem: hours
  // logged for them reach the report with no cost, and nobody gets paid.
  // (Sean, Jul 2026: a $0.00 rate is never intentional — always treat it as
  // missing.) A $0.00 row is also flagged individually below.
  const hasUsableRate = (a) => Number(a.cost_rate) > 0;
  const isUnrated = (c) => !ratesOf(c).some(hasUsableRate);
  const unratedCount = contractors.filter(isUnrated).length;

  // Resource types this contractor doesn't already have a rate for.
  const availableTypes = (c) => {
    const taken = new Set(ratesOf(c).map((a) => a.resource_type_id));
    return resourceTypes.filter((rt) => !taken.has(rt.resource_type_id));
  };

  const saveRate = async (userId, resourceTypeId, costRate) => {
    const rate = parseFloat(costRate);
    if (!Number.isFinite(rate) || rate <= 0) {
      // $0.00 is never a valid pay rate (Sean, Jul 2026). Blocking it here
      // means the warning elsewhere is for legacy/placeholder data only — you
      // can't create a new zero-rate contractor through this screen.
      toast.error('Enter a pay rate above $0.00.');
      return false;
    }
    setSavingKey(`${userId}:${resourceTypeId}`);
    try {
      const res = await api.post(`${API}/api/users/${userId}/resource-types`, {
        resource_type_id: resourceTypeId,
        cost_rate: rate,
      });
      if (res.ok) {
        toast.success('Pay rate saved.');
        await load();
        return true;
      }
      const data = await res.json().catch(() => ({}));
      toast.error(data.detail || "Couldn't save the pay rate.");
      return false;
    } catch (err) {
      toast.error("Couldn't save the pay rate.");
      return false;
    } finally {
      setSavingKey(null);
    }
  };

  const removeRate = async (userId, resourceTypeId) => {
    try {
      const res = await api.delete(
        `${API}/api/users/${userId}/resource-types/${encodeURIComponent(resourceTypeId)}`,
      );
      if (res.ok) {
        toast.success('Pay rate removed.');
        await load();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Couldn't remove the pay rate.");
      }
    } catch (err) {
      toast.error("Couldn't remove the pay rate.");
    }
  };

  const openAdd = (userId) => {
    setAddingFor(userId);
    setAddForm({ resource_type_id: '', cost_rate: '' });
  };

  const submitAdd = async (userId) => {
    if (!addForm.resource_type_id) {
      toast.error('Pick a resource type.');
      return;
    }
    const ok = await saveRate(userId, addForm.resource_type_id, addForm.cost_rate);
    if (ok) setAddingFor(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse text-boss-text-secondary">Loading contractor rates…</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-2xl font-semibold text-boss-text flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-boss-primary" />
            Contractor pay rates
          </h1>
          <p className="text-sm text-boss-text-secondary mt-1">
            What each contractor is paid, per resource type. These rates fill in
            automatically when a contractor is added to a contract.
          </p>
        </div>
      </div>

      {/* Missing-rates banner — the whole point: make the gap visible */}
      {unratedCount > 0 && (
        <div className="mt-4 mb-4 flex items-start gap-3 rounded-lg border border-amber-400 bg-amber-50 px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>
              {unratedCount} {unratedCount === 1 ? 'contractor needs' : 'contractors need'} a
              pay rate.
            </strong>{' '}
            Time logged for them will show no cost until a rate is set, and they
            can't be paid correctly. They're marked below.
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-boss-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contractors by name or email"
          className="w-full pl-9 pr-3 py-2 bg-boss-surface border border-boss-border rounded-lg text-boss-text text-sm focus:outline-none focus:ring-1 focus:ring-boss-primary"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-boss-text-secondary">
          {search
            ? 'No contractors match that search.'
            : 'No contractors yet. Add contractors from User Management, then set their rates here.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const rates = ratesOf(c);
            const unrated = isUnrated(c);
            const addable = availableTypes(c);
            return (
              <div
                key={c.user_id}
                className={`rounded-lg border bg-boss-surface ${
                  unrated ? 'border-amber-400' : 'border-boss-border'
                }`}
              >
                {/* Contractor row header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-boss-border">
                  <div className="min-w-0">
                    <div className="text-boss-text font-medium truncate">
                      {c.name || c.email}
                      {unrated && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700 font-normal">
                          <AlertTriangle className="w-3 h-3" /> no pay rate
                        </span>
                      )}
                    </div>
                    {c.name && (
                      <div className="text-xs text-boss-text-secondary truncate">{c.email}</div>
                    )}
                  </div>
                  {addable.length > 0 && addingFor !== c.user_id && (
                    <button
                      type="button"
                      onClick={() => openAdd(c.user_id)}
                      className="shrink-0 inline-flex items-center gap-1 text-sm text-boss-primary hover:underline"
                    >
                      <Plus className="w-4 h-4" /> Add rate
                    </button>
                  )}
                </div>

                {/* Existing rates */}
                <div className="divide-y divide-boss-border">
                  {rates.map((a) => (
                    <RateRow
                      key={a.resource_type_id}
                      assignment={a}
                      resourceTypes={resourceTypes}
                      saving={savingKey === `${c.user_id}:${a.resource_type_id}`}
                      onSave={(rate) => saveRate(c.user_id, a.resource_type_id, rate)}
                      onRemove={() =>
                        setConfirm({
                          open: true,
                          userId: c.user_id,
                          rtId: a.resource_type_id,
                          label: `${c.name || c.email} — ${a.resource_type_name || a.resource_type_id}`,
                        })
                      }
                    />
                  ))}

                  {rates.length === 0 && addingFor !== c.user_id && (
                    <div className="px-4 py-3 text-sm text-boss-text-secondary">
                      No rate set. {addable.length > 0 ? 'Add one to start paying this contractor.' : ''}
                    </div>
                  )}

                  {/* Add-rate row */}
                  {addingFor === c.user_id && (
                    <div className="px-4 py-3 flex items-center gap-2 bg-boss-elevated">
                      <select
                        value={addForm.resource_type_id}
                        onChange={(e) =>
                          setAddForm((f) => ({ ...f, resource_type_id: e.target.value }))
                        }
                        className="flex-1 px-3 py-2 bg-boss-surface border border-boss-border rounded-lg text-boss-text text-sm"
                      >
                        <option value="">Select resource type…</option>
                        {addable.map((rt) => (
                          <option key={rt.resource_type_id} value={rt.resource_type_id}>
                            {rt.name}
                            {rt.sell_rate != null ? ` (bills at $${rt.sell_rate}/hr)` : ''}
                          </option>
                        ))}
                      </select>
                      <div className="relative w-36">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-secondary text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={addForm.cost_rate}
                          onChange={(e) =>
                            setAddForm((f) => ({ ...f, cost_rate: e.target.value }))
                          }
                          placeholder="Pay rate"
                          className="w-full pl-6 pr-3 py-2 bg-boss-surface border border-boss-border rounded-lg text-boss-text text-sm"
                        />
                      </div>
                      <span className="text-xs text-boss-text-secondary">/hr</span>
                      <button
                        type="button"
                        onClick={() => submitAdd(c.user_id)}
                        disabled={savingKey?.startsWith(`${c.user_id}:`)}
                        className="p-2 text-boss-primary hover:bg-boss-primary/10 rounded-lg disabled:opacity-50"
                        aria-label="Save rate"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddingFor(null)}
                        className="p-2 text-boss-text-secondary hover:bg-boss-border/40 rounded-lg"
                        aria-label="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={confirm.open}
        title="Remove this pay rate?"
        message={`Remove the rate for ${confirm.label}? Time logged against this resource type will show no cost until a rate is set again.`}
        confirmText="Remove rate"
        onConfirm={async () => {
          await removeRate(confirm.userId, confirm.rtId);
          setConfirm({ open: false, userId: null, rtId: null, label: '' });
        }}
        onCancel={() => setConfirm({ open: false, userId: null, rtId: null, label: '' })}
      />
    </div>
  );
}

/** One editable rate row. Inline edit — click the figure, change it, save. */
function RateRow({ assignment, resourceTypes, saving, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(assignment.cost_rate ?? ''));

  const rt = resourceTypes.find((r) => r.resource_type_id === assignment.resource_type_id);
  const sell = rt?.sell_rate;

  const commit = async () => {
    const ok = await onSave(value);
    if (ok) setEditing(false);
  };

  return (
    <div className="px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm text-boss-text truncate">
          {assignment.resource_type_name || assignment.resource_type_id}
        </div>
        {sell != null && (
          <div className="text-xs text-boss-text-secondary">Bills to client at ${sell}/hr</div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {editing ? (
          <>
            <div className="relative w-32">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-secondary text-sm">
                $
              </span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={value}
                autoFocus
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit();
                  if (e.key === 'Escape') {
                    setValue(String(assignment.cost_rate ?? ''));
                    setEditing(false);
                  }
                }}
                className="w-full pl-6 pr-2 py-1.5 bg-boss-surface border border-boss-border rounded-lg text-boss-text text-sm"
              />
            </div>
            <button
              type="button"
              onClick={commit}
              disabled={saving}
              className="p-1.5 text-boss-primary hover:bg-boss-primary/10 rounded disabled:opacity-50"
              aria-label="Save"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setValue(String(assignment.cost_rate ?? ''));
                setEditing(false);
              }}
              className="p-1.5 text-boss-text-secondary hover:bg-boss-border/40 rounded"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            {Number(assignment.cost_rate) > 0 ? (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-sm font-medium text-boss-text tabular-nums hover:underline"
                title="Click to edit"
              >
                ${Number(assignment.cost_rate).toFixed(2)}/hr
              </button>
            ) : (
              // $0.00 is never a real pay rate. Show it as the problem it is,
              // and invite a fix, rather than rendering it as a set figure.
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:underline"
                title="Set a pay rate"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> No rate — set one
              </button>
            )}
            <button
              type="button"
              onClick={onRemove}
              className="p-1.5 text-boss-text-secondary hover:text-red-500 hover:bg-red-500/10 rounded"
              aria-label="Remove rate"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
